import {
  CaretLeft,
  CaretRight,
  DownloadSimple,
  File,
  FileDoc,
  FilePdf,
  FilePpt,
  FileXls,
} from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { getDocument, type RenderTask } from "pdfjs-dist";
import { useCallback, useEffect, useRef, useState } from "react";

interface FilePreviewBlockProps {
  url: string;
  fileName: string;
  fileType: "pdf" | "ai" | "xlsx" | "xls" | "docx" | "doc" | "pptx" | "ppt";
  pageCount?: number;
}

const FILE_TYPE_ICONS: Record<string, React.ReactNode> = {
  pdf: <FilePdf size={20} className="text-puntt-red-9" />,
  ai: <FilePdf size={20} className="text-puntt-gold-11" />,
  xlsx: <FileXls size={20} className="text-malachite-700" />,
  xls: <FileXls size={20} className="text-malachite-700" />,
  docx: <FileDoc size={20} className="text-puntt-accent-11" />,
  doc: <FileDoc size={20} className="text-puntt-accent-11" />,
  pptx: <FilePpt size={20} className="text-puntt-gold-11" />,
  ppt: <FilePpt size={20} className="text-puntt-gold-11" />,
};

const OFFICE_FILE_TYPES = new Set([
  "xlsx",
  "xls",
  "docx",
  "doc",
  "pptx",
  "ppt",
]);

function PdfPreview({ url, fileName }: { url: string; fileName: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pdfDocRef = useRef<Awaited<
    ReturnType<typeof getDocument>["promise"]
  > | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const renderGenRef = useRef(0);

  const renderPage = useCallback(async (pageNum: number) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    // PDF.js cannot render two pages into the same canvas at once. Cancel the
    // in-flight render; track generation so a superseded call can no-op cleanly.
    const myGen = ++renderGenRef.current;
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch {
        // cancel() may throw if the task already settled; safe to ignore.
      }
      renderTaskRef.current = null;
    }

    setRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      if (myGen !== renderGenRef.current) return;

      const scale = 1.5;
      const viewport = page.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext("2d");
      if (!context) {
        setError("Failed to render page");
        setRendering(false);
        return;
      }

      const task = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = task;
      await task.promise;
      if (myGen === renderGenRef.current) {
        renderTaskRef.current = null;
        setRendering(false);
      }
    } catch (err) {
      if (
        (err as { name?: string } | null)?.name ===
        "RenderingCancelledException"
      ) {
        return;
      }
      console.error("Error rendering PDF page:", err);
      if (myGen === renderGenRef.current) {
        setError("Failed to render page");
        setRendering(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      try {
        const pdfDoc = await getDocument(url).promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setTotalPages(pdfDoc.numPages);
        await renderPage(1);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading PDF:", err);
        setError("Failed to load PDF");
        setRendering(false);
      }
    }

    loadPdf();

    return () => {
      cancelled = true;
      // Bump generation so any renderPage still suspended in getPage() short-
      // circuits after resume instead of touching a detached canvas/state.
      renderGenRef.current++;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
        renderTaskRef.current = null;
      }
    };
  }, [url, renderPage]);

  useEffect(() => {
    if (pdfDocRef.current && currentPage > 0) {
      renderPage(currentPage);
    }
  }, [currentPage, renderPage]);

  if (error) {
    return (
      <DownloadFallback
        url={url}
        fileName={fileName}
        fileType="pdf"
        error={error}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-puntt-neutral-gray-6">
      {/* PDF canvas */}
      <div className="relative max-h-[600px] overflow-auto bg-puntt-neutral-gray-3 p-2">
        {rendering && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-puntt-neutral-gray-3/80">
            <Text size="2" className="text-puntt-neutral-gray-9">
              Loading...
            </Text>
          </div>
        )}
        <canvas
          ref={canvasRef}
          className="mx-auto block"
          style={{ maxWidth: "100%" }}
        />
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <Flex
          align="center"
          justify="center"
          gap="2"
          className="border-t border-puntt-neutral-gray-5 bg-base-white px-3 py-2"
        >
          <Button
            size="1"
            variant="ghost"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            className="cursor-pointer"
          >
            <CaretLeft size={14} />
          </Button>
          <Text size="1" className="text-puntt-neutral-gray-11">
            Page {currentPage} of {totalPages}
          </Text>
          <Button
            size="1"
            variant="ghost"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            className="cursor-pointer"
          >
            <CaretRight size={14} />
          </Button>
        </Flex>
      )}
    </div>
  );
}

function OfficePreview({
  url,
  fileName,
  fileType,
}: {
  url: string;
  fileName: string;
  fileType: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

  if (error) {
    return (
      <DownloadFallback url={url} fileName={fileName} fileType={fileType} />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-puntt-neutral-gray-6">
      <div className="relative" style={{ height: 600 }}>
        {!loaded && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-puntt-neutral-gray-3">
            <Text size="2" className="text-puntt-neutral-gray-9">
              Loading preview...
            </Text>
          </div>
        )}
        <iframe
          src={viewerUrl}
          title={`Preview: ${fileName}`}
          sandbox="allow-scripts allow-same-origin"
          className="size-full border-none"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      </div>
    </div>
  );
}

function DownloadFallback({
  url,
  fileName,
  fileType,
  error,
}: {
  url: string;
  fileName: string;
  fileType: string;
  error?: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-4">
      {FILE_TYPE_ICONS[fileType] ?? (
        <File size={20} className="text-puntt-neutral-gray-9" />
      )}
      <div className="flex-1">
        <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
          {fileName}
        </Text>
        {error && (
          <Text as="p" size="1" className="text-puntt-neutral-gray-9">
            Preview unavailable
          </Text>
        )}
      </div>
      <a
        href={url}
        download={fileName}
        target="_blank"
        rel="noopener noreferrer"
        className="text-white inline-flex items-center gap-1 rounded-md bg-puntt-accent-9 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-puntt-accent-10"
      >
        <DownloadSimple size={14} />
        Download
      </a>
    </div>
  );
}

export function FilePreviewBlock({
  url,
  fileName,
  fileType,
}: FilePreviewBlockProps) {
  return (
    <div>
      {/* File header */}
      <Flex align="center" gap="2" className="mb-2">
        {FILE_TYPE_ICONS[fileType] ?? (
          <File size={16} className="text-puntt-neutral-gray-9" />
        )}
        <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
          {fileName}
        </Text>
        <a
          href={url}
          download={fileName}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto"
          title="Download file"
        >
          <DownloadSimple
            size={14}
            className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
          />
        </a>
      </Flex>

      {/* Preview */}
      {fileType === "pdf" || fileType === "ai" ? (
        <PdfPreview url={url} fileName={fileName} />
      ) : OFFICE_FILE_TYPES.has(fileType) ? (
        <OfficePreview url={url} fileName={fileName} fileType={fileType} />
      ) : (
        <DownloadFallback url={url} fileName={fileName} fileType={fileType} />
      )}
    </div>
  );
}
